// TODO: (vivian) now handle by kc, remove after release
/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable @typescript-eslint/require-await */
/* eslint-disable @typescript-eslint/no-misused-promises */
import { CircularProgress } from '@ouster/webviz-react-components';

import { FormEvent, useCallback, useEffect, useState } from 'react';
import { RiGoogleFill } from 'react-icons/ri';
import { RxArrowRight } from 'react-icons/rx';
import { Link } from 'react-router-dom';

import PasswordInput from './PasswordInput';
import { useKeycloak } from '../../KeycloakContext';
import styles from '../../pages/PublicPage.module.css';
import { useStoreActions } from '../../reducers';
import { LOGIN_APPROACHES } from '../../utils/constants';

export enum AuthMode {
  SIGN_UP = 'SIGN_UP',
  REGISTER = 'REGISTER',
}

type AuthFormProps = {
  isPasswordRequirementVisible: boolean;
  isEulaAccepted: boolean;
  isSignUpRequired: boolean;
  setIsEulaVisible: (isEulaVisible: boolean) => void;
  setIsEulaAccepted?: (isEulaVisible: boolean) => void;
  authMode: AuthMode;
  setPendingAuthMethod: (value: string) => void;
  setPendingFormData: (value: FormData) => void;
  pendingAuthMethod: string | null;
  pendingFormData: FormData | null;
  authorizeError?: string;
  invitationId?: string;
  orgUuid?: string;
};

export default function AuthForm({
  isPasswordRequirementVisible,
  isEulaAccepted,
  isSignUpRequired,
  setIsEulaVisible,
  setIsEulaAccepted,
  authMode,
  setPendingAuthMethod,
  setPendingFormData,
  pendingAuthMethod,
  pendingFormData,
  authorizeError,
  invitationId,
  orgUuid,
}: AuthFormProps) {
  const { keycloak, authenticateUserWithEmailPass } = useKeycloak();
  const [errorMessage, setErrorMessage] = useState('');
  const { signUpUserWithEmail, validateInvitation } = useStoreActions(
    (actions) => actions.user
  );

  const [isPasswordRequirementMet, setIsPasswordRequirementMet] =
    useState(false);

  const authUser = useCallback(async () => {
    // authenticate with google
    if (pendingAuthMethod === LOGIN_APPROACHES.GOOGLE && keycloak) {
      setIsEulaVisible(false);
      const url = keycloak.createLoginUrl({ idpHint: LOGIN_APPROACHES.GOOGLE });
      window.location.href = url;
    }

    // create user in keycloak then auto log in user to get token
    if (pendingAuthMethod === LOGIN_APPROACHES.EMAIL) {
      const formData = pendingFormData;
      if (!formData) return;

      const email = formData.get('email') as string;
      const password = formData.get('password') as string;

      setErrorMessage('');

      let errorMsg = '';

      if (isSignUpRequired) {
        try {
          await signUpUserWithEmail({ email, password });
        } catch (error) {
          if (error instanceof Error) {
            errorMsg = error.message;
          }
        }

        try {
          await authenticateUserWithEmailPass(email, password);
        } catch (_) {
          // try to login fail, show error message from sign up
          // sign up fail
          setErrorMessage(errorMsg);
          setIsEulaVisible(false);
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          setIsEulaAccepted && setIsEulaAccepted(false);
        }
      } else {
        // user already signed up, we just need to login them
        try {
          await authenticateUserWithEmailPass(email, password);
        } catch (error) {
          if (error instanceof Error) {
            setErrorMessage(error.message);
          }
        }
      }
    }
  }, [
    authenticateUserWithEmailPass,
    isSignUpRequired,
    keycloak,
    pendingAuthMethod,
    pendingFormData,
    setIsEulaAccepted,
    setIsEulaVisible,
    signUpUserWithEmail,
  ]);

  const handleFormSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const email = formData.get('email') as string;

    // we need first validate the invitation and email
    // before try to add user to keycloak
    if (invitationId && orgUuid) {
      try {
        await validateInvitation({
          email,
          invitation_uuid: invitationId,
          org_uuid: orgUuid,
        });
      } catch (error) {
        if (error instanceof Error) {
          setErrorMessage(error.message);
        }
        return;
      }
    }

    setPendingAuthMethod(LOGIN_APPROACHES.EMAIL);

    setErrorMessage('');
    setPendingFormData(formData);

    if (authMode === AuthMode.SIGN_UP) {
      setIsEulaVisible(true);
    } else if (isSignUpRequired) {
      setIsEulaVisible(true);
    } else {
      authUser();
    }
  };

  const handleAuthWithGoogle = () => {
    setPendingAuthMethod(LOGIN_APPROACHES.GOOGLE);

    if (authMode === AuthMode.SIGN_UP) {
      setIsEulaVisible(true);
    } else {
      // in register mode, only show eula to invited user (not self sign up user).
      // eslint-disable-next-line no-lonely-if
      if (isSignUpRequired) {
        setIsEulaVisible(true);
      } else {
        authUser();
      }
    }
  };

  useEffect(() => {
    if (!isEulaAccepted) return;
    authUser();
  }, [authUser, isEulaAccepted]);

  if (isEulaAccepted) {
    return (
      <div className={styles['loading-container']}>
        <CircularProgress isIndeterminate size="50px" thickness={10} />
      </div>
    );
  }

  return (
    <>
      {(errorMessage !== '' || authorizeError) && (
        <div className={styles['error-message-with-link']}>
          {errorMessage || authorizeError}
        </div>
      )}

      <form className={styles['auth-form']} onSubmit={handleFormSubmit}>
        <p className={styles['input-header']}>
          <label htmlFor="email">
            Email address <sup>*</sup>
          </label>
        </p>
        <input
          type="email"
          name="email"
          id="email"
          placeholder="name@company.com"
          className={styles['input-field']}
        />

        <p className={styles['input-header']}>
          <label htmlFor="email">
            Password <sup>*</sup>
          </label>
        </p>
        <PasswordInput
          required
          placeholder="Enter a strong password"
          name="password"
          id="password"
          showRequirements={isPasswordRequirementVisible}
          setIsPasswordRequirementMet={
            isPasswordRequirementVisible
              ? setIsPasswordRequirementMet
              : undefined
          }
        />

        <button
          type="submit"
          className={styles['primary-button']}
          style={{ placeContent: 'space-between' }}
          disabled={isPasswordRequirementVisible && !isPasswordRequirementMet}
        >
          {authMode === AuthMode.SIGN_UP
            ? 'Create account'
            : 'Join Organization'}
          <RxArrowRight />
        </button>
      </form>

      <p style={{ textAlign: 'center' }}>or</p>

      <button
        type="button"
        className={styles['secondary-button']}
        onClick={handleAuthWithGoogle}
      >
        <RiGoogleFill size={24} /> <span>Continue with Google</span>
      </button>

      {/* Login button */}
      <p className={styles['footer-link']} style={{ marginTop: 'unset' }}>
        Already have an account?{' '}
        <Link to="/login" className={styles['link-button']}>
          Log in
        </Link>
      </p>

      {/* Legal links */}
      <p className={styles['footer-copy']} style={{ marginTop: '2rem' }}>
        By proceeding, you agree to the{' '}
        <a
          href="https://ouster.com/terms-of-use"
          target="_blank"
          rel="noreferrer"
          className={styles['link-button']}
        >
          Terms of Service
        </a>{' '}
        and{' '}
        <a
          href="https://ouster.com/privacy-policy"
          target="_blank"
          rel="noreferrer"
          className={styles['link-button']}
        >
          Privacy Policy
        </a>
      </p>
    </>
  );
}
